import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BrandMark } from "../design/BrandMark";

import { requests, send } from "../api/auth";
import { StatusChip } from "../design/StatusChip";
import { LocaleProvider, preSessionLocale, useT } from "../i18n/LocaleProvider";
import { homePathFor } from "../session/access";
import { useSession } from "../session/SessionProvider";
import { translateLabel } from "../i18n/translate";
import { SURFACE_PREFIX } from "../system/navigation";

// ---------------------------------------------------------------------------
// Choosing an organisation.
//
// THIS SCREEN EXISTS BECAUSE OF RULE A1, AND IT IS THE SCREEN THAT MAKES A1
// SURVIVABLE.
//
// A person can be a scheduler at one agency and a worker at another. V3 decided
// that a login with several memberships resolves to NO active organisation —
// "the first one" is precisely the shape A1 forbids, and a default tenant is
// how one agency's data ends up on another agency's screen.
//
// The cost of that decision is a state the user has to resolve, and without
// this screen the cost would be paid as a dead end: signed in, no organisation,
// nothing on the page. So the state gets a screen with the choice on it.
//
// Choosing issues a NEW SESSION at the server — a privilege change, rotated
// like any other (§4.1) — which is why this posts rather than setting anything
// locally. There is nothing local to set: the client has no way to name an
// organisation on a request, by design.
// ---------------------------------------------------------------------------

/**
 * S75. Signed in but in no organisation yet: the person's own stored language when
 * the server has said it, else the browser's (the rule Protected applies to workers).
 */
export function ChooseOrg() {
  const { me } = useSession();
  return (
    <LocaleProvider locale={preSessionLocale(me?.authenticated ? me.locale : null)}>
      <ChooseOrgScreen />
    </LocaleProvider>
  );
}

function ChooseOrgScreen() {
  const { t, locale } = useT();
  const { me, apiBase, reload } = useSession();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const memberships = me?.authenticated ? me.memberships : [];

  async function choose(orgId: string) {
    if (busy) return;
    setBusy(orgId);
    setError(null);
    try {
      const { status } = await send(requests.selectOrg(apiBase, orgId));
      if (status !== 200) {
        setError(t("That organisation is not available to you any more. Ask an administrator."));
        return;
      }
      const next = await reload();
      navigate((next && homePathFor(next)) ?? SURFACE_PREFIX.console, { replace: true });
    } catch {
      // A failed fetch must not leave every button disabled and unexplained —
      // on this screen that would strand somebody signed in with no way to
      // reach any organisation at all.
      setError(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(null);
    }
  }

  return (
    <main className="centre-page">
      <BrandMark />
      <div className="centre-card">
        <h1>{t("Choose an organisation")}</h1>
        {/* The real reason when there is nothing to choose (somebody removed from every organisation), and the
            next step as a link: an empty list under "you belong to more than one" was a contradiction. */}
        {memberships.length === 0 ? (
          <>
            <p className="muted">{t("You do not belong to any organisation at the moment. An invitation link from an agency brings you in, or you can create your own organisation.")}</p>
            <Link className="btn btn-block" to="/sign-up">
              {t("Create an organisation")}
            </Link>
          </>
        ) : (
          <p className="muted">{t("You belong to more than one. Pick the one you are working in.")}</p>
        )}

        <div className="form-message" role="status" aria-live="polite">
          {error ? (
            <>
              <StatusChip tone="failed" label={t("Refused")} />
              <span>{error}</span>
            </>
          ) : null}
        </div>

        <div className="stack">
          {memberships.map((membership) => (
            <button
              key={membership.org_id}
              className="btn btn-secondary btn-block btn-two-line"
              type="button"
              disabled={busy !== null}
              onClick={() => void choose(membership.org_id)}
            >
              {busy === membership.org_id ? (
                t("Working…")
              ) : (
                <>
                  <span>{membership.name}</span>
                  <small>{translateLabel(locale, membership.role_label)}</small>
                </>
              )}
            </button>
          ))}
        </div>
      </div>
    </main>
  );
}
